// ── SUMMER WORKSHOPS / CLASSES: shared admin Catalog CRUD ──────────────
// Both are type='workshop' rows differentiated by workshopGroup ('summer' vs
// 'tap_voice'), with identical capacity/waitlist/archive/duplicate mechanics
// on the Catalog tab -- only copy and a couple of field/payload shapes
// differ (Classes bills monthly with a picked set of months; Workshops has
// a flat one-time price). Every one of those differences is transcribed
// here into CATALOG_GROUP rather than re-derived, so it can be checked
// against the two original screens value-by-value.
//
// The Roster tab is deliberately NOT unified with this: Workshops' promote
// charges immediately ("Promote & charge", /workshops/{id}/promote),
// Classes' promote never charges (/tap-voice/{id}/promote, the monthly
// billing cron picks it up) -- a real behavioral difference, not
// duplication, so it stays as its own separate code in admin-workshops.js /
// admin-tapvoice.js.
//
// TV_MONTH_NAMES/tvMonthlyLabel live here (not in admin-tapvoice.js) because
// catalogModal()/CATALOG_GROUP.tap_voice.priceLabel — both defined in this
// file — need them. tvMonthlyLabel is also read by admin-tapvoice.js's
// Classes Preview (imported below) and parent-classes.js's real p-tapvoice
// registration view, which is why it's window-exposed at the bottom in
// addition to being exported.
//
// tvPrograms/tvEligiblePrograms/tvIsFull live here for the same reason:
// registration-window and eligibility logic read by three separate domains
// (admin-tapvoice.js's Classes Preview, parent-dashboard.js's dashboard
// banner, parent-classes.js's real registration flow). tvWindowOpen is also
// read by admin-settings.js now — all four consumers use real imports, so
// only tvMonthlyLabel/tvEligiblePrograms/tvIsFull still need window
// exposure below (unrelated pre-existing over-exposure, not touched here).
//
// catalogSetFilter/toggleCatalogActive/saveCatalogItem all rewrite the same
// tabsEl/listEl pair directly (bypassing route()'s VIEW_INIT lifecycle,
// same as admin-billing.js's updateReports()) — wireCatalogGroup() is the
// shared re-wire step all three call afterward, and it's also what each
// consumer (admin-workshops.js, admin-tapvoice.js) calls from its own
// VIEW_INIT for the initial render. catalogModal's Save button is attached
// directly inline (imperative function with control over the modal DOM it
// just wrote via openModal, same pattern as billing-shared.js's cardModal).
import { apiFetch } from '../core/api.js';
import { DB, PROGRAMS, currentView, session } from '../core/state.js';
import { canEdit } from '../core/domain-utils.js';
import { TODAY, calcAge, readMoney, moneyValue } from '../core/format.js';
import { openModal, closeModal, toast, toastError, ICON_COPY, ICON_ARCHIVE, ICON_RESTORE } from '../core/ui.js';
import { refresh } from '../core/routing.js';

export function tvWindowOpen() {
  const s = DB.settings;
  if (s.tapVoiceManualClosed) return false;
  if (!s.tapVoiceOpensAt) return false;
  if (TODAY < new Date(s.tapVoiceOpensAt)) return false;
  if (s.tapVoiceClosesAt && TODAY > new Date(s.tapVoiceClosesAt)) return false;
  return true;
}
export function tvPrograms() {
  return PROGRAMS.filter(p => p.billingType === 'workshop' && p.workshopGroup === 'tap_voice' && p.active !== false && p.visibleToParents !== false);
}
export function tvEligiblePrograms(performer) {
  const age = calcAge(performer.dob);
  return tvPrograms().filter(p => {
    const min = p.ageMin ?? 0;
    const max = p.ageMax ?? 99;
    return age >= min && age <= max;
  });
}
export function tvIsFull(p) {
  if (p.capacity === null || p.capacity === undefined) return false;
  const season = Number(DB.settings.upcomingSeasonYear);
  const reg = (p.enrollmentCounts || []).find(c => Number(c.seasonYear) === season && c.status === 'registered');
  return (reg ? reg.count : 0) >= p.capacity;
}

// New, separate function (not shared with renderProgramList/programModal/
// saveProgram in admin-settings) so PC/BB's existing Programs screen is never touched.
function workshopFillLabel(p) {
  if (!p.enrollmentCounts) return 'No registrations yet';
  const season = Number(DB.settings.upcomingSeasonYear);
  const reg = p.enrollmentCounts.find(c => Number(c.seasonYear) === season && c.status === 'registered');
  const wait = p.enrollmentCounts.find(c => Number(c.seasonYear) === season && c.status === 'waitlisted');
  const regCount = reg ? reg.count : 0;
  const waitCount = wait ? wait.count : 0;
  const label = p.capacity ? `${regCount} / ${p.capacity} registered` : `${regCount} registered`;
  return label + (waitCount ? ` · ${waitCount} waitlisted` : '');
}
export const TV_MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
// "$X/mo × N months (Sep, Oct, Nov)" — Classes bill monthly like PC/BB, not a
// flat one-time price, so this is distinct from priceLabel()/programOneTimePrice()
// which Summer Workshops (workshopGroup==='summer') still use unchanged.
export function tvMonthlyLabel(p) {
  const months = p.seasonMonths || [];
  if (!p.monthly || !months.length) return p.monthly ? fmt$(p.monthly) + '/mo' : 'No fee set';
  const names = months.map(m => TV_MONTH_NAMES[m - 1]).join(', ');
  return `${fmt$(p.monthly)}/mo × ${months.length} mo (${names}) = ${fmt$(p.monthly * months.length)} total`;
}

const CATALOG_GROUP = {
  summer: {
    listElId: 'workshopCatalogList', tabsElId: 'workshopCatalogTabs',
    emptyActive: 'No active workshops — add one to get started.',
    emptyArchived: 'No archived workshops.',
    priceLabel: p => fmt$(p.price),
    fillLabel: p => workshopFillLabel(p),
    modalTitleNew: 'New workshop', modalTitleEdit: 'Edit workshop',
    nameLabel: 'Workshop name', namePlaceholder: 'e.g. Beginning Tap',
    categoryPlaceholder: 'e.g. Dance',
    rateLabel: 'Price', ratePlaceholder: 'e.g. 100',
    capacityHint: '(soft cap — blank for unlimited)',
    datesPlaceholder: 'e.g. July 14–16', durationPlaceholder: 'e.g. 3-Day Workshop',
    timePlaceholder: 'e.g. 1:30–2:45 PM', teacherPlaceholder: 'e.g. Taught by Lori',
    descPlaceholder: 'Shown on the catalog page and detail view',
    notePlaceholder: 'e.g. Session time is scheduled directly with Staci after you register.',
    activeCheckboxLabel: 'Active — not archived',
    saveLabelNew: 'Create workshop',
    nameRequiredMsg: 'Workshop name is required',
    viewId: 'a-workshops-catalog', updatedToast: 'Workshop updated',
    hasMonthPicker: false,
  },
  tap_voice: {
    listElId: 'tvCatalogList', tabsElId: 'tvCatalogTabs',
    emptyActive: 'No active Tap or Voice offerings — add one to get started.',
    emptyArchived: 'No archived offerings.',
    priceLabel: p => tvMonthlyLabel(p),
    fillLabel: p => p.capacity ? workshopFillLabel(p) : 'Unlimited',
    modalTitleNew: 'New Tap/Voice offering', modalTitleEdit: 'Edit offering',
    nameLabel: 'Name', namePlaceholder: 'e.g. Voice — Ms. Smith',
    categoryPlaceholder: 'e.g. Tap or Voice',
    rateLabel: 'Monthly rate', ratePlaceholder: 'e.g. 160',
    capacityHint: '(soft cap — blank for unlimited; use this for a Voice teacher\'s number of slots, leave blank for Tap)',
    datesPlaceholder: 'e.g. Tuesdays, starting Sept 9', durationPlaceholder: 'e.g. Weekly through May',
    timePlaceholder: 'e.g. Assigned in September', teacherPlaceholder: 'e.g. Ms. Smith',
    descPlaceholder: 'Shown to parents when they register',
    notePlaceholder: 'e.g. Exact lesson time assigned directly by the teacher in September.',
    activeCheckboxLabel: 'Active — not archived',
    saveLabelNew: 'Create offering',
    nameRequiredMsg: 'Name is required',
    viewId: 'a-tapvoice-catalog', updatedToast: 'Offering updated',
    hasMonthPicker: true,
  },
};
// Active vs archived is just the existing `active` flag — archiving is
// nothing more than an admin-list view filter, not a new state. Persists
// across re-renders like the other view-toggle state vars in this file.
// Each group remembers its own Active/Archived tab independently.
//
// Separate from that: `visibleToParents` (Open/Close registration, toggled
// via toggleCatalogVisibility() below) controls whether parents can see and
// register for an active item, mirroring store_sales' visibleToParents /
// Publish-Unpublish in admin-store.js. The two are independent — an active,
// closed-to-registration class still appears in Active here and stays fully
// manageable on the Roster tab; only archiving (this flag) removes it from
// both.
let catalogFilter = { summer: 'active', tap_voice: 'active' };
function catalogSetFilter(group, mode) {
  catalogFilter[group] = mode;
  const cfg = CATALOG_GROUP[group];
  const listEl = document.getElementById(cfg.listElId);
  if (listEl) listEl.innerHTML = renderCatalogList(group);
  const tabsEl = document.getElementById(cfg.tabsElId);
  if (tabsEl) tabsEl.outerHTML = renderCatalogTabs(group);
  wireCatalogGroup(group);
}
// Re-attaches listeners for the tab buttons and list-row actions (Edit,
// Duplicate, Archive/Restore) — called after every direct rewrite of
// tabsEl/listEl above, and by each consumer's own VIEW_INIT for the initial
// render (those buttons live in the consumer's own returned template, not
// written by this file, so they need their own explicit wire-up call too).
export function wireCatalogGroup(group) {
  const cfg = CATALOG_GROUP[group];
  const tabsEl = document.getElementById(cfg.tabsElId);
  if (tabsEl) tabsEl.querySelectorAll('[data-action="catalog-set-filter"]').forEach(btn => {
    btn.addEventListener('click', () => catalogSetFilter(btn.dataset.group, btn.dataset.mode));
  });
  const listEl = document.getElementById(cfg.listElId);
  if (listEl) {
    listEl.querySelectorAll('[data-action="catalog-edit"]').forEach(btn => {
      btn.addEventListener('click', () => catalogModal(btn.dataset.group, btn.dataset.program));
    });
    listEl.querySelectorAll('[data-action="catalog-duplicate"]').forEach(btn => {
      btn.addEventListener('click', () => duplicateCatalogItem(btn.dataset.group, btn.dataset.program));
    });
    listEl.querySelectorAll('[data-action="catalog-toggle-active"]').forEach(btn => {
      btn.addEventListener('click', () => toggleCatalogActive(btn.dataset.group, btn.dataset.program));
    });
    listEl.querySelectorAll('[data-action="catalog-toggle-visibility"]').forEach(btn => {
      btn.addEventListener('click', () => toggleCatalogVisibility(btn.dataset.group, btn.dataset.program));
    });
  }
}
export function renderCatalogTabs(group) {
  const cfg = CATALOG_GROUP[group];
  const all = PROGRAMS.filter(p => p.billingType === 'workshop' && p.workshopGroup === group);
  const activeCount = all.filter(p => p.active !== false).length;
  const archivedCount = all.filter(p => p.active === false).length;
  const filter = catalogFilter[group];
  const tab = (mode, label, count) => `<button data-action="catalog-set-filter" data-group="${group}" data-mode="${mode}" style="padding:7px 14px;border-radius:7px;border:1px solid ${filter === mode ? 'rgba(201,168,76,0.4)' : 'var(--border-soft)'};background:${filter === mode ? 'rgba(201,168,76,0.08)' : 'transparent'};color:${filter === mode ? 'var(--gold-light)' : 'var(--muted)'};font-size:13px;font-family:inherit;cursor:pointer">${label} (${count})</button>`;
  return `<div id="${cfg.tabsElId}" style="display:flex;gap:6px;margin-bottom:14px">${tab('active', 'Active', activeCount)}${tab('archived', 'Archived', archivedCount)}</div>`;
}
export function renderCatalogList(group) {
  const cfg = CATALOG_GROUP[group];
  const filter = catalogFilter[group];
  const list = PROGRAMS
    .filter(p => p.billingType === 'workshop' && p.workshopGroup === group && (filter === 'active' ? p.active !== false : p.active === false))
    .sort((a, b) => a.name.localeCompare(b.name));
  if (!list.length) return `<p style="font-size:13px;color:var(--muted);font-style:italic;padding:8px 0">${filter === 'active' ? cfg.emptyActive : cfg.emptyArchived}</p>`;
  return list.map((p, i) => `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 0;${i < list.length - 1 ? 'border-bottom:1px solid var(--border-soft)' : ''}">
      <div>
        <div style="font-size:14px;color:${p.active === false ? 'var(--muted)' : 'var(--cream)'}">${esc(p.name)}${p.active !== false && p.visibleToParents === false ? ' <span class="pill pill-amber">Registration closed</span>' : ''}</div>
        <div style="font-size:12px;color:var(--muted);margin-top:2px">
          ${cfg.priceLabel(p)}${(p.details && p.details.category) ? ' · ' + esc(p.details.category) : ''}
          ${p.ageMin !== null || p.ageMax !== null ? ' · Ages ' + (p.ageMin !== null ? p.ageMin : '') + (p.ageMin !== null && p.ageMax !== null ? '–' : '') + (p.ageMax !== null ? p.ageMax : '+') : ''}
          · ${cfg.fillLabel(p)}
        </div>
      </div>
      ${canEdit(session) ? `<div style="display:flex;gap:8px;flex-shrink:0">
        ${p.active !== false ? `<button class="btn-ghost btn-sm" data-action="catalog-toggle-visibility" data-group="${group}" data-program="${p.id}">${p.visibleToParents === false ? 'Open registration' : 'Close registration'}</button>` : ''}
        <button class="btn-ghost btn-sm" data-action="catalog-edit" data-group="${group}" data-program="${p.id}">Edit</button>
        <button class="icon-btn" title="Duplicate into a new season" aria-label="Duplicate into a new season" data-action="catalog-duplicate" data-group="${group}" data-program="${p.id}">${ICON_COPY}</button>
        <button class="icon-btn" title="${p.active === false ? 'Restore to active' : 'Archive'}" aria-label="${p.active === false ? 'Restore to active' : 'Archive'}" data-action="catalog-toggle-active" data-group="${group}" data-program="${p.id}">${p.active === false ? ICON_RESTORE : ICON_ARCHIVE}</button>
      </div>` : ''}
    </div>`).join('');
}
async function toggleCatalogActive(group, programId) {
  const p = PROGRAMS.find(x => x.id === programId);
  if (!p) return;
  const cfg = CATALOG_GROUP[group];
  const newActive = p.active === false;
  try {
    const updated = await apiFetch('/programs/' + programId, { method: 'PUT', body: JSON.stringify({ active: newActive, workshopGroup: group }) });
    const idx = PROGRAMS.findIndex(x => x.id === programId);
    if (idx >= 0) PROGRAMS[idx] = updated;
    const listEl = document.getElementById(cfg.listElId);
    if (listEl) listEl.innerHTML = renderCatalogList(group);
    const tabsEl = document.getElementById(cfg.tabsElId);
    if (tabsEl) tabsEl.outerHTML = renderCatalogTabs(group);
    wireCatalogGroup(group);
    toast(newActive ? p.name + ' restored to active' : p.name + ' archived');
  } catch (e) { toastError(e.message); }
}
// Separate from Active/Archived: lets Lori stop new signups on a class that's
// still actively running (full, pausing enrollment, etc.) without pulling it
// off the Roster tab or her own management views — see the comment above
// catalogFilter for why this is a second, independent flag.
async function toggleCatalogVisibility(group, programId) {
  const p = PROGRAMS.find(x => x.id === programId);
  if (!p) return;
  const cfg = CATALOG_GROUP[group];
  const newVisible = p.visibleToParents === false;
  try {
    const updated = await apiFetch('/programs/' + programId, { method: 'PUT', body: JSON.stringify({ visibleToParents: newVisible, workshopGroup: group }) });
    const idx = PROGRAMS.findIndex(x => x.id === programId);
    if (idx >= 0) PROGRAMS[idx] = updated;
    const listEl = document.getElementById(cfg.listElId);
    if (listEl) listEl.innerHTML = renderCatalogList(group);
    wireCatalogGroup(group);
    toast(newVisible ? p.name + ' open for registration' : p.name + ' closed to registration');
  } catch (e) { toastError(e.message); }
}
// Year-to-year: copy everything except the schedule into a fresh row, so Lori
// has to consciously re-enter dates/time before it goes live. The old row is
// left untouched (and should already be inactive by the time this is used).
function duplicateCatalogItem(group, programId) {
  const p = PROGRAMS.find(x => x.id === programId);
  if (!p) return;
  const d = p.details || {};
  const prefill = {
    name: p.name, ageMin: p.ageMin, ageMax: p.ageMax, capacity: p.capacity,
    details: { category: d.category, dates: '', time: '', duration: d.duration, teacher: d.teacher, description: d.description, note: d.note }
  };
  if (group === 'tap_voice') { prefill.monthly = p.monthly; prefill.seasonMonths = p.seasonMonths; }
  else { prefill.price = p.price; }
  catalogModal(group, null, prefill);
}
export function catalogModal(group, programId, prefill) {
  const cfg = CATALOG_GROUP[group];
  const p = programId ? PROGRAMS.find(x => x.id === programId) : (prefill || null);
  const d = (p && p.details) || {};
  const currentSeason = cfg.hasMonthPicker && p && p.seasonMonths ? p.seasonMonths : [];
  openModal(`
    <h3>${programId ? cfg.modalTitleEdit : cfg.modalTitleNew}</h3>
    <div class="field"><label>${cfg.nameLabel}</label><input type="text" id="pcmName" value="${p ? esc(p.name) : ''}" placeholder="${cfg.namePlaceholder}"></div>
    <div class="field-row">
      <div class="field"><label>Category</label><input type="text" id="pcmCategory" value="${esc(d.category || '')}" placeholder="${cfg.categoryPlaceholder}"></div>
      <div class="field"><label>${cfg.rateLabel}</label>
        <div class="money"><span class="cur" aria-hidden="true">$</span><input type="text" inputmode="decimal" id="pcmRate" value="${moneyValue(p ? (cfg.hasMonthPicker ? p.monthly : p.price) : '')}" placeholder="${cfg.ratePlaceholder}" aria-label="${cfg.rateLabel} in dollars"></div></div>
    </div>
    ${cfg.hasMonthPicker ? `<div class="field">
      <label>Active (billed) months <span style="font-size:10px;color:var(--muted);font-weight:400;text-transform:none;letter-spacing:0">(exactly which months to charge — sets both when billing starts and how many charges)</span></label>
      <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px">
        ${TV_MONTH_NAMES.map((m, i) => {
          const monthNum = i + 1;
          const checked = currentSeason.includes(monthNum);
          return `<label style="display:flex;align-items:center;gap:4px;padding:4px 10px;border-radius:20px;border:1px solid ${checked ? 'rgba(201,168,76,0.4)' : 'var(--border-soft)'};background:${checked ? 'rgba(201,168,76,0.08)' : 'transparent'};cursor:pointer;font-size:12px;color:${checked ? 'var(--gold-light)' : 'var(--muted)'}">
            <input type="checkbox" name="pcmMonth" value="${monthNum}" ${checked ? 'checked' : ''} style="width:auto;accent-color:var(--gold)"> ${m}</label>`;
        }).join('')}
      </div>
    </div>` : ''}
    <div class="field-row">
      <div class="field"><label>Min age</label><input type="number" id="pcmAgeMin" min="0" max="25" value="${p && p.ageMin !== null ? p.ageMin : ''}" placeholder="e.g. 9"></div>
      <div class="field"><label>Max age <span class="plain-cap">(blank for none)</span></label><input type="number" id="pcmAgeMax" min="0" max="25" value="${p && p.ageMax !== null ? p.ageMax : ''}" placeholder="e.g. 18"></div>
    </div>
    <div class="field"><label>Capacity <span class="plain-cap">${cfg.capacityHint}</span></label><input type="number" id="pcmCapacity" min="1" value="${p && p.capacity !== null ? p.capacity : ''}" placeholder="e.g. 6"></div>
    <div class="field-row">
      <div class="field"><label>Dates</label><input type="text" id="pcmDates" value="${esc(d.dates || '')}" placeholder="${cfg.datesPlaceholder}"></div>
      <div class="field"><label>Duration</label><input type="text" id="pcmDuration" value="${esc(d.duration || '')}" placeholder="${cfg.durationPlaceholder}"></div>
    </div>
    <div class="field-row">
      <div class="field"><label>Time</label><input type="text" id="pcmTime" value="${esc(d.time || '')}" placeholder="${cfg.timePlaceholder}"></div>
      <div class="field"><label>Teacher</label><input type="text" id="pcmTeacher" value="${esc(d.teacher || '')}" placeholder="${cfg.teacherPlaceholder}"></div>
    </div>
    <div class="field"><label>Description</label><textarea id="pcmDesc" rows="4" style="background:var(--bg-input);border:1px solid var(--border-soft);border-radius:7px;padding:11px 13px;color:var(--cream);font-family:'Source Sans 3',sans-serif;font-size:14px;font-weight:300;line-height:1.6;outline:none;width:100%;resize:vertical" onfocus="this.style.borderColor='var(--gold)'" onblur="this.style.borderColor='var(--border-soft)'" placeholder="${cfg.descPlaceholder}">${esc(d.description || '')}</textarea></div>
    <div class="field"><label>Note <span class="plain-cap">(optional, shown in italics)</span></label><input type="text" id="pcmNote" value="${esc(d.note || '')}" placeholder="${cfg.notePlaceholder}"></div>
    ${programId ? `<div class="field" style="flex-direction:row;align-items:center;gap:10px;margin-top:4px">
      <input type="checkbox" id="pcmActive" style="width:auto" ${p.active !== false ? 'checked' : ''}>
      <label style="text-transform:none;letter-spacing:0;font-size:13px;color:var(--cream)">${cfg.activeCheckboxLabel}</label>
    </div>
    <p style="font-size:12px;color:var(--muted);margin-top:2px">Whether parents can register is controlled separately by Open/Close registration on the Catalog list.</p>` : ''}
    <button class="btn" id="catalogSaveBtn" style="margin-top:10px">${programId ? 'Save changes' : cfg.saveLabelNew}</button>`);
  document.getElementById('catalogSaveBtn').addEventListener('click', () => saveCatalogItem(group, programId || ''));
}
async function saveCatalogItem(group, programId) {
  const cfg = CATALOG_GROUP[group];
  const name = document.getElementById('pcmName').value.trim();
  const category = document.getElementById('pcmCategory').value.trim();
  const rate = readMoney(document.getElementById('pcmRate'));
  if (rate === null) { toastError(cfg.rateLabel + ' must be a dollar amount, like 100 or 100.00'); return; }
  const ageMinRaw = document.getElementById('pcmAgeMin').value.trim();
  const ageMaxRaw = document.getElementById('pcmAgeMax').value.trim();
  const ageMin = ageMinRaw !== '' ? parseInt(ageMinRaw) : null;
  const ageMax = ageMaxRaw !== '' ? parseInt(ageMaxRaw) : null;
  const capacityRaw = document.getElementById('pcmCapacity').value.trim();
  const capacity = capacityRaw !== '' ? parseInt(capacityRaw) : null;
  const dates = document.getElementById('pcmDates').value.trim();
  const duration = document.getElementById('pcmDuration').value.trim();
  const time = document.getElementById('pcmTime').value.trim();
  const teacher = document.getElementById('pcmTeacher').value.trim();
  const description = document.getElementById('pcmDesc').value.trim();
  const note = document.getElementById('pcmNote').value.trim();
  if (!name) { toast(cfg.nameRequiredMsg); return; }
  const payload = {
    name, type: 'workshop', workshopGroup: group, ageMin, ageMax, capacity,
    details: { category, dates, duration, time, teacher, description, note }
  };
  if (cfg.hasMonthPicker) {
    const monthChecks = [...document.querySelectorAll('input[name="pcmMonth"]:checked')].map(cb => parseInt(cb.value));
    if (!monthChecks.length) { toast('Pick at least one billed month'); return; }
    payload.monthly = rate;
    payload.seasonMonths = monthChecks;
  } else {
    payload.price = rate;
  }
  try {
    if (programId) {
      const activeEl = document.getElementById('pcmActive');
      payload.active = activeEl ? activeEl.checked : true;
      const updated = await apiFetch('/programs/' + programId, { method: 'PUT', body: JSON.stringify(payload) });
      const idx = PROGRAMS.findIndex(x => x.id === programId);
      if (idx >= 0) PROGRAMS[idx] = updated;
    } else {
      const created = await apiFetch('/programs', { method: 'POST', body: JSON.stringify(payload) });
      PROGRAMS.push(created);
    }
    closeModal();
    if (currentView === cfg.viewId) {
      const listEl = document.getElementById(cfg.listElId);
      if (listEl) listEl.innerHTML = renderCatalogList(group);
      const tabsEl = document.getElementById(cfg.tabsElId);
      if (tabsEl) tabsEl.outerHTML = renderCatalogTabs(group);
      wireCatalogGroup(group);
    } else refresh();
    toast(programId ? cfg.updatedToast : name + ' added');
  } catch (e) { toastError(e.message); }
}

Object.assign(window, {
  tvMonthlyLabel, tvEligiblePrograms, tvIsFull,
});
